export type DashboardTransaction = {
  id: string;
  parent_id?: string | null;
  date: string;
  amount: number;
  category?: string | null;
  categoryName?: string | null;
  categoryIncome?: boolean | null;
  account: string;
  accountName?: string | null;
  payeeName?: string | null;
  notes?: string | null;
};

export function transactionKind(row: DashboardTransaction) {
  if (row.categoryIncome === true) return row.amount < 0 ? '收入冲正' : '收入';
  if (row.categoryIncome === false) return row.amount > 0 ? '退款' : '支出';
  return row.amount > 0 ? '未分类流入' : '未分类支出';
}

export function shiftMonth(month: string, offset: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const date = new Date(year, monthNumber - 1 + offset, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function summarizeDashboard(
  rows: ReadonlyArray<DashboardTransaction>,
  selectedMonth: string,
) {
  const months = Array.from({ length: 12 }, (_, index) => {
    const month = shiftMonth(selectedMonth, index - 11);
    return {
      month,
      label: `${Number(month.slice(5))}月`,
      income: 0,
      expense: 0,
    };
  });
  const monthMap = new Map(months.map(month => [month.month, month]));
  const categories = new Map<
    string,
    { id: string; name: string; amount: number }
  >();
  const selectedRows: DashboardTransaction[] = [];
  const transactionIds = new Set<string>();
  let uncategorized = 0;
  let grossExpense = 0;
  let refunds = 0;
  let incomeCorrections = 0;
  let hasHistory = false;

  // Amounts remain integer minor units throughout aggregation. Split children
  // arrive once from AQL's `splits: inline`; their parent is never added again.
  for (const row of rows) {
    const month = monthMap.get(row.date.slice(0, 7));
    if (!month || !Number.isSafeInteger(row.amount)) continue;
    hasHistory = true;
    // A credit to an expense category is a refund, not new income. Likewise
    // a debit to an income category reverses income. Unclassified cash flows
    // temporarily follow their sign until the user assigns a category.
    const isIncome =
      row.categoryIncome === true ||
      (row.categoryIncome == null && row.amount > 0);
    if (isIncome) month.income += row.amount;
    else month.expense -= row.amount;
    if (month.month !== selectedMonth) continue;

    selectedRows.push(row);
    transactionIds.add(row.parent_id || row.id);
    if (isIncome && row.amount < 0) incomeCorrections -= row.amount;
    if (!isIncome && row.amount > 0) refunds += row.amount;
    if (!isIncome && row.amount < 0) {
      grossExpense -= row.amount;
      const id = row.category || 'uncategorized';
      const category = categories.get(id) || {
        id,
        name: row.categoryName || '未分类',
        amount: 0,
      };
      category.amount -= row.amount;
      categories.set(id, category);
      if (!row.category) uncategorized += 1;
    }
  }

  const current = months[months.length - 1];
  const categoryList = [...categories.values()].sort(
    (first, second) => second.amount - first.amount,
  );
  // Keep every category in the total, even when the legend groups the tail.
  const visibleCategories = categoryList.slice(0, 5);
  if (categoryList.length > 5) {
    visibleCategories.push({
      id: '__other',
      name: '其他分类',
      amount: categoryList.slice(5).reduce((sum, item) => sum + item.amount, 0),
    });
  }

  return {
    months,
    income: current.income,
    expense: current.expense,
    grossExpense,
    refunds,
    incomeCorrections,
    net: current.income - current.expense,
    transactionCount: transactionIds.size,
    uncategorized,
    categories: visibleCategories,
    recent: selectedRows
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 6),
    hasHistory,
  };
}
